import http from 'hub-http/clients/apiClient';
import orderBy from 'hs-lodash/orderBy';
import { useApolloClient } from '@apollo/client';
import { useCallback } from 'react';
import { useStartProgressMutation, useMarkContentCompletedMutation } from '../../../__generated__/chirp/com/hubspot/academy/user/progress/rpc/UserProgressWriterChirpService.hooks';
import { MARK_CONTENT_COMPLETED_MUTATION } from '../../../__generated__/chirp/com/hubspot/academy/user/progress/rpc/UserProgressWriterChirpService.dfc.new';
import { GET_ALL_USER_PROGRESS_BY_USER_ID_QUERY, GET_USER_TRACK_PROGRESS_QUERY, GET_USER_LESSON_PROGRESS_QUERY } from '../../../__generated__/chirp/com/hubspot/userprogress/bff/rpc/UserProgressBFFChirpService.dfc.new';
import { useGetAllUserProgressByUserIdQuery, useGetUserProgressQuery, useGetUserTrackProgressQuery, useGetUserLessonProgressQuery } from '../../../__generated__/chirp/com/hubspot/userprogress/bff/rpc/UserProgressBFFChirpService.hooks';
import getLang from '../../utils/getLang';
import { registerQuery, useQuery, unwrapHubHttpError } from '../../utils/DataFetchingClient';
import { BASE_URI_PROGRESS } from '../../constants/uri';
import { convertToSupportedLanguage } from '../../constants/academySupportedLanguages';
import { updateCache } from '../../utils/DataFetchingClientCache.new';
import { getPlaylistIdFromUrl } from '../../utils/urlParams';
import { MY_LEARNING_QUERY_FIELD_NAME } from '../myLearning/myLearningQueries';
const isTrackProgressResponse = progress => progress.type === 'TRACK';
export const GET_PLAYLIST_PROGRESS = registerQuery({
  fieldName: 'playlistProgress',
  args: ['playlistId'],
  fetcher({
    playlistId
  }) {
    return http.get(`${BASE_URI_PROGRESS}/user-progress/playlist/${playlistId}`).then(response => {
      const progress = response;
      return Object.assign({}, progress, {
        itemsProgress: orderBy(progress.itemsProgress, ['updatedAt'], ['desc'])
      });
    });
  }
});
export const usePlaylistProgress = (playlistId, skip) => {
  const {
    data: playlistProgress,
    loading: playlistProgressLoading,
    error: playlistProgressError
  } = useQuery(GET_PLAYLIST_PROGRESS, {
    variables: {
      playlistId
    },
    skip
  });
  return {
    playlistProgress,
    playlistProgressLoading,
    playlistProgressError
  };
};
export const useStartPlaylistProgress = ({
  playlistId,
  language
}) => {
  const playlistIdToUse = playlistId || getPlaylistIdFromUrl();
  const [startPlaylistProgress, {
    loading: startPlaylistProgressLoading,
    called: startPlaylistProgressCalled,
    error: startPlaylistProgressCalledError
  }] = useStartProgressMutation({
    variables: {
      contentId: playlistId,
      contentType: 'PLAYLIST',
      language: convertToSupportedLanguage(language)
    },
    refetchQueries: [{
      query: GET_ALL_USER_PROGRESS_BY_USER_ID_QUERY,
      variables: {
        language: convertToSupportedLanguage(language)
      },
      context: {
        timeout: 25000
      }
    }, ...(playlistIdToUse ? [{
      query: GET_PLAYLIST_PROGRESS,
      variables: {
        playlistId: playlistIdToUse
      }
    }] : [])],
    update: (cache, result) => {
      const {
        data,
        errors
      } = result;
      if (!data || errors && errors.length) {
        return;
      }
      cache.evict({
        fieldName: MY_LEARNING_QUERY_FIELD_NAME
      });
    }
  });
  return [startPlaylistProgress, {
    startPlaylistProgressLoading,
    startPlaylistProgressCalled,
    startPlaylistProgressCalledError
  }];
};
export const useAllProgress = (variables, options = {}) => {
  const {
    language
  } = variables || {};
  const {
    data: allProgress,
    loading: allProgressLoading,
    error: allProgressError
  } = useGetAllUserProgressByUserIdQuery({
    variables: {
      language: convertToSupportedLanguage(language || getLang())
    },
    fetchPolicy: options.fetchPolicy,
    pollInterval: options.polling ? 1000 : 0,
    skip: options.skip,
    context: {
      timeout: options.timeout || 25000
    }
  });
  return {
    allProgress: allProgress || {
      assetProgress: [],
      contentProgress: {}
    },
    allProgressLoading,
    allProgressError
  };
};
export const useStartTrackProgress = ({
  language,
  trackId,
  playlistId
}) => {
  const playlistIdToUse = playlistId || getPlaylistIdFromUrl();
  const [startTrackProgress, {
    loading: startTrackProgressLoading,
    called: startTrackProgressCalled,
    error: startTrackProgressError
  }] = useStartProgressMutation({
    variables: {
      contentId: trackId,
      contentType: 'TRACK',
      playlistId,
      language: convertToSupportedLanguage(language)
    },
    refetchQueries: [{
      query: GET_ALL_USER_PROGRESS_BY_USER_ID_QUERY,
      variables: {
        language: convertToSupportedLanguage(language)
      },
      context: {
        timeout: 25000
      }
    }, {
      query: GET_USER_TRACK_PROGRESS_QUERY,
      variables: {
        trackId,
        includeExpiredAwards: true
      }
    }, ...(playlistIdToUse ? [{
      query: GET_PLAYLIST_PROGRESS,
      variables: {
        playlistId: playlistIdToUse
      }
    }] : [])],
    update: (cache, result) => {
      const {
        data,
        errors
      } = result;
      if (!data || errors && errors.length) return;
      cache.evict({
        fieldName: MY_LEARNING_QUERY_FIELD_NAME
      });
    }
  });
  return [startTrackProgress, {
    startTrackProgressLoading,
    startTrackProgressCalled,
    startTrackProgressError
  }];
};
export const useStartLessonProgress = ({
  language,
  lessonId,
  playlistId
}) => {
  const playlistIdToUse = playlistId || getPlaylistIdFromUrl();
  const [startLessonProgress, {
    loading: startLessonProgressLoading,
    called: startLessonProgressCalled,
    error: startLessonProgressError
  }] = useStartProgressMutation({
    variables: {
      contentId: lessonId,
      contentType: 'LESSON',
      playlistId,
      language: convertToSupportedLanguage(language)
    },
    refetchQueries: [{
      query: GET_ALL_USER_PROGRESS_BY_USER_ID_QUERY,
      variables: {
        language: convertToSupportedLanguage(language)
      },
      context: {
        timeout: 25000
      }
    }, {
      query: GET_USER_LESSON_PROGRESS_QUERY,
      variables: {
        lessonId
      }
    }, ...(playlistIdToUse ? [{
      query: GET_PLAYLIST_PROGRESS,
      variables: {
        playlistId: playlistIdToUse
      }
    }] : [])],
    update: (cache, result) => {
      const {
        data,
        errors
      } = result;
      if (!data || errors && errors.length) return;
      cache.evict({
        fieldName: MY_LEARNING_QUERY_FIELD_NAME
      });
    }
  });
  return [startLessonProgress, {
    startLessonProgressLoading,
    startLessonProgressCalled,
    startLessonProgressError
  }];
};
export const useAssetStatus = ({
  id,
  type,
  language,
  skip
}) => {
  let status = 'NOT_STARTED';
  const {
    allProgress,
    allProgressLoading,
    allProgressError
  } = useAllProgress({
    language
  }, {
    skip
  });
  if (!allProgressLoading) {
    const progress = allProgress.assetProgress.find(asset => {
      var _String;
      return asset.type === type && asset.id === id && (!language || ((_String = String(asset.language)) === null || _String === void 0 ? void 0 : _String.toLowerCase()) === language.toLowerCase());
    });
    if (progress) {
      status = progress.status;
    }
  }
  return {
    status,
    allProgressLoading,
    allProgressError
  };
};
export const useTrackProgressById = trackId => {
  const client = useApolloClient();
  const {
    data: trackProgressById,
    loading: trackProgressByIdLoading,
    error: trackProgressByIdError,
    refetch: trackProgressByIdRefetch
  } = useGetUserTrackProgressQuery({
    context: {
      timeout: 25000
    },
    onCompleted: data => data.result.progress.forEach(lessonProgress => updateCache(GET_USER_LESSON_PROGRESS_QUERY, () => lessonProgress, {
      lessonId: lessonProgress.lessonId
    }, client)),
    skip: !trackId || isNaN(Number(trackId)),
    variables: {
      trackId: trackId,
      includeExpiredAwards: true
    }
  });
  return {
    trackProgressById,
    trackProgressByIdLoading,
    trackProgressByIdError: unwrapHubHttpError(trackProgressByIdError),
    trackProgressByIdRefetch
  };
};
export const useLessonProgressById = lessonId => {
  const {
    data: lessonProgressById,
    loading: lessonProgressByIdLoading,
    error: lessonProgressByIdError,
    refetch: lessonProgressByIdRefetch
  } = useGetUserLessonProgressQuery({
    context: {
      timeout: 25000
    },
    variables: {
      lessonId: Number(lessonId)
    },
    skip: !lessonId || isNaN(Number(lessonId))
  });
  return {
    lessonProgressById,
    lessonProgressByIdLoading,
    lessonProgressByIdError: unwrapHubHttpError(lessonProgressByIdError),
    lessonProgressByIdRefetch
  };
};
export const useTrackProgress = ({
  options = {},
  trackId
}) => {
  const {
    data: userProgressData,
    loading,
    error
  } = useGetUserProgressQuery({
    context: {
      timeout: options.timeout
    },
    fetchPolicy: options.fetchPolicy,
    pollInterval: options.polling ? 1000 : 0,
    skip: options.skip,
    variables: {
      requestedProgress: [{
        id: trackId,
        type: 'TRACK'
      }]
    }
  });
  const trackProgress = userProgressData === null || userProgressData === void 0 ? void 0 : userProgressData.progress.find(isTrackProgressResponse);
  return {
    trackProgress,
    trackProgressLoading: loading,
    trackProgressError: unwrapHubHttpError(error)
  };
};
export const useUpdateContentProgress = () => {
  const [mutate, {
    loading: updateContentProgressLoading,
    called: updateContentProgressCalled,
    error: updateContentProgressError,
    data: updateContentProgressData
  }] = useMarkContentCompletedMutation({
    update: (cache, result) => {
      const {
        data,
        errors
      } = result;
      if (!data || errors && errors.length) {
        return;
      }
      cache.evict({
        fieldName: MY_LEARNING_QUERY_FIELD_NAME
      });
    }
  });
  const updateContentProgress = useCallback(({
    contentId,
    lessonId,
    trackId,
    language
  }) => {
    const playlistId = getPlaylistIdFromUrl();
    return mutate({
      awaitRefetchQueries: true,
      context: {
        timeout: 25000
      },
      variables: {
        contentId,
        lessonId,
        trackId,
        playlistId,
        progressType: 'COMPLETED',
        language: convertToSupportedLanguage(language),
        source: 'ACADEMY'
      },
      refetchQueries: [{
        query: GET_ALL_USER_PROGRESS_BY_USER_ID_QUERY,
        variables: {
          language: convertToSupportedLanguage(language)
        },
        context: {
          timeout: 25000
        }
      }, ...(lessonId ? [{
        query: GET_USER_LESSON_PROGRESS_QUERY,
        variables: {
          lessonId
        }
      }] : []), ...(trackId ? [{
        query: GET_USER_TRACK_PROGRESS_QUERY,
        variables: {
          trackId,
          includeExpiredAwards: true
        }
      }] : []), ...(playlistId ? [{
        query: GET_PLAYLIST_PROGRESS,
        variables: {
          playlistId
        }
      }] : [])]
    });
  }, [mutate]);
  return [updateContentProgress, {
    updateContentProgressLoading,
    updateContentProgressCalled,
    updateContentProgressError,
    updateContentProgressData
  }];
};
export const updateContentProgress = ({
  contentId,
  lessonId,
  trackId,
  language
}, client) => {
  const playlistId = getPlaylistIdFromUrl();
  return client.mutate({
    mutation: MARK_CONTENT_COMPLETED_MUTATION,
    context: {
      timeout: 25000
    },
    variables: {
      contentId,
      lessonId,
      trackId,
      playlistId,
      progressType: 'COMPLETED',
      language: convertToSupportedLanguage(language),
      source: 'ACADEMY'
    },
    refetchQueries: [{
      query: GET_ALL_USER_PROGRESS_BY_USER_ID_QUERY,
      variables: {
        language: convertToSupportedLanguage(language)
      },
      context: {
        timeout: 25000
      }
    }, ...(lessonId ? [{
      query: GET_USER_LESSON_PROGRESS_QUERY,
      variables: {
        lessonId
      }
    }] : []), ...(trackId ? [{
      query: GET_USER_TRACK_PROGRESS_QUERY,
      variables: {
        trackId,
        includeExpiredAwards: true
      }
    }] : []), ...(playlistId ? [{
      query: GET_PLAYLIST_PROGRESS,
      variables: {
        playlistId
      }
    }] : [])],
    update: (cache, result) => {
      const {
        data,
        errors
      } = result;
      if (!data || errors && errors.length) {
        return;
      }
      cache.evict({
        fieldName: MY_LEARNING_QUERY_FIELD_NAME
      });
    }
  }).then(result => result.data);
};